import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import ReactSelect from "react-select";
import { Link } from "react-router-dom";
import { withRouter } from '../../../common/withRouter';
import { ButtonOBSOLETED, Notification, Message } from "../../../web.common";
import { LayoutContext } from "../../layout/siteLayout";
import PageTop from "../../layout/PageTop";
import withApi from "../../higherOrderComponents/withApi";
import { DisplayDate } from "../../items";
import { apiErrorText, formatMinor, Pager } from "./shared";

const pageSize = 50;

const statusOptions = ["draft", "open", "confirmed", "cancelled", "expired"].map((value) => ({ value, label: value }));
const formOptions = ["game", "suggestion", "shout"].map((value) => ({ value, label: value }));

const emptyFilters = { clubhouseSlug: "", status: null, form: null, includeDeleted: false, from: "", to: "" };

const Wannas = ({ api }) => {
  const [filters, setFilters] = useState(emptyFilters);
  const [applied, setApplied] = useState(emptyFilters);
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [notification, setNotification] = useState(null);

  const loadData = async (filtersToUse, nextSkip) => {
    try {
      const result = await api.clubhouseAdmin.getWannas({
        clubhouseSlug: filtersToUse.clubhouseSlug.trim() || null,
        status: filtersToUse.status?.value || null,
        form: filtersToUse.form?.value || null,
        includeDeleted: filtersToUse.includeDeleted,
        take: pageSize,
        skip: nextSkip,
      });
      setRows(result.wannas);
      setTotal(result.total);
    } catch (e) {
      setNotification(new Message("error", apiErrorText(e, "Unable to load the wannas.")));
    }
  };

  useEffect(() => {
    loadData(emptyFilters, 0);
  }, []);

  const search = (e) => {
    e?.preventDefault();
    setApplied(filters);
    setSkip(0);
    loadData(filters, 0);
  };

  const changePage = (nextSkip) => {
    setSkip(nextSkip);
    loadData(applied, nextSkip);
  };

  // The API has no date filter: the date range is applied to the rows of the current page only.
  const dateMatches = (wanna) => {
    const day = wanna.startsAtUtc ? wanna.startsAtUtc.substring(0, 10) : null;
    if (applied.from && (!day || day < applied.from)) return false;
    if (applied.to && (!day || day > applied.to)) return false;
    return true;
  };
  const visibleRows = rows ? rows.filter(dateMatches) : null;
  const hasDateFilter = !!(applied.from || applied.to);

  const input = (name, extra = {}) => (
    <input
      name={name}
      value={filters[name]}
      onChange={({ target }) => setFilters({ ...filters, [name]: target.value })}
      {...extra}
    />
  );

  return (
    <LayoutContext.Consumer>
      {() => (
        <React.Fragment>
          <Notification newMessage={notification} />
          <div className="content-with-right-pane-delimiter">
            <PageTop documentTitle="Clubhouse wannas" documentDescription="Clubhouse wannas" />
            <h2 className="main-page-header">Clubhouse wannas</h2>

            <form onSubmit={search} style={{ display: "flex", gap: "1rem", alignItems: "flex-end", flexWrap: "wrap", margin: "1rem 0" }}>
              <label>
                Clubhouse (slug)<br />
                {input("clubhouseSlug", { placeholder: "koebenhavn" })}
              </label>
              <label>
                Status<br />
                <span style={{ display: "inline-block", minWidth: "10rem" }}>
                  <ReactSelect
                    isClearable
                    placeholder="Any"
                    value={filters.status}
                    onChange={(status) => setFilters({ ...filters, status })}
                    options={statusOptions}
                  />
                </span>
              </label>
              <label>
                Form<br />
                <span style={{ display: "inline-block", minWidth: "10rem" }}>
                  <ReactSelect
                    isClearable
                    placeholder="Any"
                    value={filters.form}
                    onChange={(form) => setFilters({ ...filters, form })}
                    options={formOptions}
                  />
                </span>
              </label>
              <label>
                Starts from (UTC date)<br />
                <input type="date" value={filters.from} onChange={({ target }) => setFilters({ ...filters, from: target.value })} />
              </label>
              <label>
                Starts until (UTC date)<br />
                <input type="date" value={filters.to} onChange={({ target }) => setFilters({ ...filters, to: target.value })} />
              </label>
              <label>
                <input type="checkbox" checked={filters.includeDeleted} onChange={({ target }) => setFilters({ ...filters, includeDeleted: target.checked })} /> Include deleted
              </label>
              <ButtonOBSOLETED type="submit" buttonContent="Search" />
            </form>
            {hasDateFilter && (
              <p>The date range applies to the rows of this page only (the API has no date filter).</p>
            )}

            {visibleRows == null ? (
              <span>Loading...</span>
            ) : (
              <article className="data-table-wrap">
                <table className="data-lists component-data-table cell-content-align-top">
                  <thead>
                    <tr>
                      <td>Code</td>
                      <td>Clubhouse</td>
                      <td>Form</td>
                      <td>Status</td>
                      <td>Text</td>
                      <td>Creator</td>
                      <td>Joined / interested</td>
                      <td>Paid / refunded</td>
                      <td>Starts</td>
                      <td>Deleted</td>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((wanna) => (
                      <tr key={wanna.code}>
                        <td><Link to={`/clubhouse-wanna/${encodeURIComponent(wanna.code)}`}>{wanna.code}</Link>{wanna.isDemo && " (demo)"}</td>
                        <td><Link to={`/clubhouse/${encodeURIComponent(wanna.clubhouseSlug)}`}>{wanna.clubhouseSlug}</Link></td>
                        <td>{wanna.form}</td>
                        <td>{wanna.status}</td>
                        <td>{wanna.text}</td>
                        <td>{wanna.creator?.name}</td>
                        <td>{wanna.joinedCount} / {wanna.interestedCount}</td>
                        <td>{formatMinor(wanna.paidMinor ?? 0, wanna.currencyIsoCode)} / {formatMinor(wanna.refundedMinor ?? 0, wanna.currencyIsoCode)}</td>
                        <td><DisplayDate>{wanna.startsAtUtc}</DisplayDate></td>
                        <td>{wanna.deletedAtUtc ? "yes" : "no"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </article>
            )}
            <Pager skip={skip} take={pageSize} total={rows ? total : null} onChange={changePage} />
          </div>
        </React.Fragment>
      )}
    </LayoutContext.Consumer>
  );
};

const WannasPage = withRouter(
  withApi(
    connect(
      ({ user }) => ({ isLoggedIn: !!user }),
      {}
    )(Wannas)
  )
);

WannasPage.requiresLogin = true;
WannasPage.propTypes = { isLoggedIn: PropTypes.bool };

export default WannasPage;
